export function sanitizeErrorToken(token) {
  if (typeof token !== "string") {
    return "";
  }
  return token
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function resolveI18nTranslator(i18nInstance) {
  const candidate =
    i18nInstance && i18nInstance.global ? i18nInstance.global : i18nInstance;

  return {
    te(key) {
      return !!(candidate && typeof candidate.te === "function" && candidate.te(key));
    },
    t(key) {
      if (candidate && typeof candidate.t === "function") {
        return candidate.t(key);
      }
      return key;
    }
  };
}

const AUTH_REDIRECT_ERROR_CODES = new Set([
  "ERR_BAD_JWT",
  "ERR_BAD_AUTH",
  "ERR_NOT_ALLOWED"
]);

function normalizeApiErrorPayload(data) {
  if (!data) {
    return null;
  }

  if (data && typeof data === "object" && data.error) {
    return data;
  }

  if (
    data &&
    typeof data === "object" &&
    data.records &&
    typeof data.records === "object" &&
    data.records.error
  ) {
    return data.records;
  }

  if (typeof data === "string") {
    return { error: data };
  }

  return null;
}

export function buildFallbackDescriptionKey(errorCode, value) {
  if (typeof errorCode !== "string") {
    return "";
  }
  const family = sanitizeErrorToken(errorCode.replace(/^ERR_/, ""));
  if (!family) {
    return "";
  }
  const detail = sanitizeErrorToken(value);
  return detail ? `errors.${family}.${detail}` : `errors.${family}`;
}

export function extractApiErrorPayload(message) {
  if (!message) {
    return null;
  }

  const directPayload = normalizeApiErrorPayload(message.data);
  if (directPayload) {
    return directPayload;
  }

  const responsePayload = normalizeApiErrorPayload(
    message.response && message.response.data
  );
  if (responsePayload) {
    return responsePayload;
  }

  const wrappedPayload = normalizeApiErrorPayload(
    message.e && message.e.response && message.e.response.data
  );
  if (wrappedPayload) {
    return wrappedPayload;
  }

  return null;
}

export function extractApiErrorCode(message) {
  const payload = extractApiErrorPayload(message);
  return payload && typeof payload.error === "string" ? payload.error : "";
}

export function isAuthRedirectError(message) {
  return AUTH_REDIRECT_ERROR_CODES.has(extractApiErrorCode(message));
}

export function redirectToLoginOnAuthError(
  message,
  router,
  route = "/login"
) {
  if (!isAuthRedirectError(message)) {
    return false;
  }

  if (router && typeof router.replace === "function") {
    router.replace(route);
    return true;
  }

  if (typeof window !== "undefined" && window.location.hash !== "#/login") {
    window.location.replace("#/login");
  }

  return true;
}

export function mapApiErrorToMessage(payload, i18nInstance) {
  const translator = resolveI18nTranslator(i18nInstance);

  if (!payload || typeof payload !== "object") {
    return "";
  }

  if (
    typeof payload.description_key === "string" &&
    translator.te(payload.description_key)
  ) {
    return translator.t(payload.description_key);
  }

  const fallbackKey = buildFallbackDescriptionKey(payload.error, payload.value);
  if (fallbackKey && translator.te(fallbackKey)) {
    return translator.t(fallbackKey);
  }

  if (
    typeof payload.description === "string" &&
    payload.description.length > 0
  ) {
    return payload.description;
  }

  if (typeof payload.error === "string") {
    const apiKey = `errors.api.${payload.error}`;
    return translator.te(apiKey) ? translator.t(apiKey) : payload.error;
  }

  return "";
}

export function resolveApiErrorMessage(message, i18nInstance) {
  const translator = resolveI18nTranslator(i18nInstance);
  const payload = extractApiErrorPayload(message);
  if (payload) {
    const mappedMessage = mapApiErrorToMessage(payload, i18nInstance);
    if (mappedMessage && typeof mappedMessage === "string") {
      return mappedMessage;
    }
  }
  return translator.t("errors.api.ERR_SERVICE");
}
